import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  HostListener,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CustomKeyboardComponent } from '@stockcare-components/custom-keyboard/custom-keyboard.component';
import { KeyboardTemplate } from '@stockcare-components/custom-keyboard/keyboard-templates';
import { Roles } from '@stockcare-helpers/roles';
import { IDevice } from '@stockcare-models/IDevice';
import { ActivityService } from '@stockcare-services/activity-service/activity.service';
import { ApplicationInsightsService } from '@stockcare-services/application-insights/application-insights.service';
import { ApplicationUpdateService } from '@stockcare-services/application-update-service/application-update.service';
import { AuthenticationService } from '@stockcare-services/authentication/authentication.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { ThemeService } from '@stockcare-services/theme/theme.service';
import { User } from '@stockcare-storage/user/user';
import { UserStorageService } from '@stockcare-storage/user/user-storage.service';
import { RemoveUserConfirmDialogComponent } from './remove-user-confirm-dialog/remove-user-confirm-dialog.component';

@Component({
  standalone: false,
  selector: 'app-pin-login',
  templateUrl: './login-pin.component.html',
  styleUrls: ['./login-pin.component.scss'],
})
export class LoginPinComponent implements OnInit, AfterViewInit {
  @ViewChild('usernameKeyboard')
  public usernameKeyboard: CustomKeyboardComponent;
  @ViewChild('pinKeyboard') public pinKeyboard: CustomKeyboardComponent;
  @ViewChild('passwordKeyboard')
  public passwordKeyboard: CustomKeyboardComponent;
  @ViewChild('usernameInput') public usernameInput: ElementRef;
  @ViewChild('passwordInput') public passwordInput: ElementRef;

  @Input() public pinActive: boolean;

  @Output() showLogIn: EventEmitter<any> = new EventEmitter();
  @Output() createPin: EventEmitter<any> = new EventEmitter();

  public logo: string;
  public device: IDevice;
  public isMobile: boolean;
  public deviceType: string = '';
  allUserNames: string[] = [];
  filteredUserNames: string[] = [];
  recentUsersNames: string[] = [];
  sorterUserNames: string[] = [];
  searchText: string = '';
  username: string = '';
  public passwordIcon: string = '../../../assets/img/password-lock.svg';
  public passwordPlaceholder = 'Password';
  public showUsernameKeyboard: boolean = false;
  public showPinKeyboard: boolean = false;
  public showPasswordKeyboard: boolean = false;
  public focusedInput: any;
  public inputtype: string = 'password';
  public passwordButtonText: string = 'SHOW';
  public showClearUsernameInput: boolean = false;
  public showSignInButton: boolean = false;
  public isButtonDisabled: boolean = true;
  public pinLength: number;
  public pin: string = '';
  public pinEntry: string = '';
  public isDeviceDeactivated: boolean;
  public pinError: boolean;
  public passwordError: boolean;
  public viewToRender: string = 'username';
  alert: string;

  public standardTemplate = KeyboardTemplate.StandardWithSpaceAndEnter;
  public pinTemplate = KeyboardTemplate.Pin;

  private readonly _topRecentUsersCount: number = 4;

  constructor(
    private router: Router,
    private userStorageService: UserStorageService,
    private settingsService: SettingsService,
    private authService: AuthenticationService,
    private activityService: ActivityService,
    private applicationInsightsService: ApplicationInsightsService,
    public deviceService: DeviceService,
    themeService: ThemeService,
    private applicationUpdateService: ApplicationUpdateService,
    private readonly dialog: MatDialog
  ) {
    if (themeService.getTheme() != null) {
      this.logo = '../../../assets/img/' + themeService.getTheme().logo;
    } else {
      this.logo = '../../../assets/img/stockcare-default-logo.png';
    }
    this.pinLength = this.settingsService.getSettingNumber('userPinLength');
  }

  ngOnInit() {
    this.getUsers();

    this.isMobile = this.deviceService.detectIsMobileDevice();
  }

  navigateBack() {
    this.viewToRender = 'username';
  }

  private getUsers() {
    this.getSessionUsers().then((users) => {
      if (users.length > 0) {
        this.allUserNames = users.map((user) => user.Username);
        
        this.recentUsersNames = users
          .sort(function (a, b) {
            return b.LastAuthenticationDate.getTime() - a.LastAuthenticationDate.getTime();
          })
          .map((user, index) => {
            if (index < this._topRecentUsersCount) return user.Username;
          })
          .filter((userName) => userName);

        this.sorterUserNames = this.filteredUserNames = [...this.recentUsersNames];
      } else {
        this.allUserNames = [];
        this.recentUsersNames = [];
        this.sorterUserNames = [];
        this.filteredUserNames = [];
      }
    });
  }

  ngAfterViewInit(): void {
    this.getDeviceDetails();
  }

  async getSessionUsers(): Promise<User[]> {
    return await this.userStorageService.getAllUsers();
  }

  showLoginScreen() {
    this.pinError = false;
    this.showLogIn.emit();
  }

  getDeviceDetails() {
    this.device = JSON.parse(localStorage.getItem('device'));

    if (this.device != null) {
      this.deviceType = this.deviceService.buildDeviceName(this?.device);
    }
  }

  private validUsernameInput(key) {
    return /^[0-9a-zA-Z]{1}$/.test(key) || /^[!@#$%^&_+\/?]{1}$/.test(key);
  }

  private validPinInput(key) {
    return /^[0-9]{1}$/.test(key);
  }

  focusInput() {
    setTimeout(() => {
      this.usernameInput?.nativeElement?.focus();
    }, 0);
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    switch (this.viewToRender) {
      case 'username':
        this.handleUsernameKeyboardEvent(event);
        break;
      case 'pin':
        this.handlePinKeyboardEvent(event);
        break;
      case 'password':
        this.handlePasswordKeyboardEvent(event);
        break;
    }
  }

  private handleUsernameKeyboardEvent(event) {
    if (this.validUsernameInput(event.key)) this.searchText += event.key;
    else if (event.key === 'Backspace') this.searchText = this.searchText.slice(0, -1);
    this.usernameKeyboard.keyboard.setInput(this.searchText);
    this.filterUserNames(this.searchText);
    this.usernameKeyboard.onChange(this.searchText);
  }

  private handlePinKeyboardEvent(event) {
    if (this.validPinInput(event.key)) this.pin += event.key;
    else if (event.key === 'Backspace') this.pin = this.pin.slice(0, -1);
    this.pinKeyboard.keyboard.setInput(this.pin);
    this.setPinEntryText();
    this.pinLogin(this.pin);
    this.pinKeyboard.onChange(this.pin);
  }

  private handlePasswordKeyboardEvent(event) {
    if (event.key === 'Enter' && !this.isButtonDisabled) {
      this.passwordLogin();
    }
  }

  public usernameKeyboardInput(inputText: string): void {
    if (this.viewToRender === 'password') {
      this.focusedInput.value = inputText;
    } else {
      this.searchText = inputText;
      this.filterUserNames(this.searchText);
    }

    this.pinError = false;
    this.passwordError = false;

    if (this.searchText !== '') {
      this.showClearUsernameInput = true;
    } else {
      this.showClearUsernameInput = false;
    }

    if (!this.pinActive && this.focusedInput.value === '') {
      this.showSignInButton = false;
    }
  }

  public pinKeyboardInput(pin: string): void {
    this.pin = pin;
    this.setPinEntryText();
    this.pinLogin(pin);
  }

  pinLogin(pin: string) {
    if (this.username == '') {
      return;
    }

    if (pin?.length === this.pinLength) {
      this.authService.loginWithPin(this.username, pin).subscribe((isValidPin) => {
        if (isValidPin) {
          this.pinError = false;
          this.onLoggedIn();
        } else {
          this.pinError = true;
        }
        this.pin = '';
        this.setPinEntryText();
        if (this.focusedInput) {
          this.focusedInput.value = '';
        }
        this.pinKeyboard.keyboard.setInput('');
        if (this.passwordInput) {
          setTimeout(() => {
            this.passwordInput.nativeElement.blur();
          }, 250);
        }
      });
    } else {
      this.pinError = false;
    }
  }

  public passwordKeyboardInput(password: string): void {
    this.focusedInput.value = password;
    this.setIsButtonDisabled();
    this.passwordError = false;
    this.pinError = false;
  }

  private setIsButtonDisabled() {
    if (this.passwordInput.nativeElement.value !== '') {
      this.isButtonDisabled = false;
    } else {
      this.isButtonDisabled = true;
    }
  }

  public passwordLogin() {
    const loginBody = {
      username: this.usernameInput.nativeElement.value.toLowerCase(),
      password: this.passwordInput.nativeElement.value,
      deviceId: this.deviceService.getDeviceId(),
    };
    this.authService.login(loginBody).subscribe(
      (isLoggedIn) => {
        if (isLoggedIn) {
          this.applicationInsightsService.logEvent('logged-in-with-username', {
            success: true,
            deviceId: loginBody.deviceId,
          });
          this.openPinKeyboardDialog(loginBody.username);
        }
      },
      (err) => {
        this.applicationInsightsService.logEvent('logged-in-with-username', {
          success: false,
          deviceId: loginBody.deviceId,
        });
        this.passwordError = true;
      }
    );
  }

  reload() {
    this.applicationUpdateService.reload();
  }

  public selectUserName(userName: string): void {
    this.username = userName.replace(/<[^>]*>?/gm, '');
    this.pin = '';
    this.setPinEntryText();
    this.viewToRender = 'pin';
  }

  private setPinEntryText() {
    this.pinEntry = '';
    for (let i = 0; i < this.pinLength; i++) {
      if (i < this.pin.length) this.pinEntry += ' \u{2022}';
      else this.pinEntry += ' -';
    }
  }

  private openPinKeyboardDialog(userName: string): void {
    this.createPin.emit(userName);
  }

  public onUsernameFocus(event): void {
    this.showUsernameKeyboard = true;
    this.syncWithUsernameKeyboard();
  }

  private syncWithUsernameKeyboard(): void {
    this.focusedInput = event.target;
    this.usernameKeyboard.keyboard.setInput(this.focusedInput.value);
    if (!this.pinActive) {
      this.sorterUserNames = [];
    } else {
      this.usernameKeyboard.onChange(this.focusedInput.value);
      this.usernameKeyboard.value = this.focusedInput.value;
    }
  }

  public onUsernameBlur(): void {
    this.showUsernameKeyboard = false;
    this.focusedInput = null;
    if (!this.pinActive) {
      this.sorterUserNames = this.filteredUserNames = [...this.recentUsersNames];
    }

    if (!this.pinActive && this.usernameInput.nativeElement.value != '') {
      this.showSignInButton = true;
      this.filteredUserNames = [];
    } else {
      this.showSignInButton = false;
    }
  }

  public onPasswordFocus(event): void {
    this.focusedInput = event.target;
    if (this.pinActive) {
      this.showPinKeyboard = true;
    } else {
      this.showPasswordKeyboard = true;
    }
    this.syncWithPasswordKeyboard();
  }

  public syncWithPasswordKeyboard(): void {
    if (!this.pinActive) {
      this.passwordKeyboard.keyboard.setInput(this.focusedInput.value);
      this.setIsButtonDisabled();
    }
  }

  public onPasswordBlur(): void {
    this.showPinKeyboard = false;
    this.showPasswordKeyboard = false;
    this.focusedInput = null;
  }

  public showPassword($event): void {
    $event.preventDefault();
    if (this.inputtype === 'password') {
      this.inputtype = 'text';
      this.passwordButtonText = 'HIDE';
    } else {
      this.inputtype = 'password';
      this.passwordButtonText = 'SHOW';
    }
  }

  public clearInput(): void {
    this.usernameInput.nativeElement.value = '';
    this.showClearUsernameInput = false;
    this.showSignInButton = false;
    this.recentUsersNames = this.sorterUserNames;
    this.filteredUserNames = this.sorterUserNames;
    this.usernameKeyboard.clearValue();
    this.passwordError = false;
    this.pinError = false;
  }

  clearSearch() {
    this.searchText = '';
    this.recentUsersNames = this.sorterUserNames;
    this.filteredUserNames = this.sorterUserNames;
    this.usernameKeyboard.clearValue();
    this.filterUserNames(this.searchText);
  }

  removeUser(username: string) {
    const dialogReference = this.dialog.open(RemoveUserConfirmDialogComponent, {
      data: {
        username: username,
      },
      width: '308px',
      disableClose: true,
    });

    dialogReference.afterClosed().subscribe(async (result) => {
      if (result) {
        this.searchText = '';
        this.getSessionUsers().then((users) => {
          const user = users.find((x) => x.Username === username);
          this.userStorageService.deleteUser(user.Id).then(() => {
            this.getUsers();
          });
        });
      }
    });
  }

  public returnToUserSelect() {
    this.viewToRender = 'username';
    this.pinError = false;
    this.pin = '';
  }

  private filterUserNames(text: string): void {
    if (text === '') {
      this.recentUsersNames = this.sorterUserNames;
      this.filteredUserNames = [];
      return;
    }

    this.filteredUserNames = [
      ...this.allUserNames
        .filter((x) => !text || x.toLowerCase().includes(text.toLowerCase()))
        .filter((username, index) => index < this._topRecentUsersCount)
        ?.map((x) => x.replace(new RegExp(text, 'ig'), '<span class="filtered-text">' + text + '</span>')),
    ];
  }

  private onLoggedIn() {
    if (this.authService.isActionCenterLogin) {
      this.navigateToActionCenter();
      return;
    }
    const isLinked = this.deviceService.isDeviceLinked();
    const isAdministrator = this.authService.isAdministrator();

    if (!isLinked) {
      this.linkDevice(isAdministrator);
      return;
    }

    if (this.deviceService.isDeviceRemoved()) {
      this.activateDevice();
      return;
    }

    this.activityService.start();

    if (this.deviceService.isWhiteboardDevice()) {
      this.navigateToWhiteBoard();
      return;
    }

    if (this.deviceService.isTheaterDevice()) {
      this.navigateToTheatre();
      return;
    }

    if (this.deviceService.isEmergencyCupboardDevice()) {
      this.navigateToEmergencyCupboard();
      return;
    }

    var canViewPatients = this.authService.isInRole([
      Roles.systemAdministrator,
      Roles.superAdministrator,
      Roles.stockIssuer,
      Roles.creditRequester,
    ]);

    if (canViewPatients) {
      this.router.navigate(['app/patients']);
      return;
    }

    var canApproveCredits = this.authService.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.creditApprover]);

    if (canApproveCredits) {
      this.router.navigate(['app/approvals']);
      return;
    }

    this.router.navigate(['app/baskets']);
    return;
  }

  private navigateToActionCenter() {
    this.router.navigate(['/app/action-center']);
  }

  private navigateToWhiteBoard() {
    this.router.navigate(['app/whiteboard']);
  }

  private navigateToTheatre() {
    this.router.navigate(['app/theatre']);
  }

  private navigateToEmergencyCupboard() {
    this.router.navigate(['app/emergency-cupboard']);
  }

  private linkDevice(isAdministrator: boolean) {
    this.router.navigate([isAdministrator ? 'app/authentication/linkdevice' : 'app/authentication/deviceneedstobelinked']);
  }

  private activateDevice() {
    this.isDeviceDeactivated = true;
    this.pinError = true;
    this.alert = 'This device has been deactivated. Contact your administrator.';
    this.router.navigate(['app/authentication/devicedeactivated']);
  }
}
